import React from 'react';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import {
  DialogContent,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { Alert } from '@mui/material';
import LocalizedStrings from 'react-localization';
import { localisedText } from '../../../lib/localisation.es6';
import { PRIMARY_THEME_COLOR_SHADE_1 } from '../../../lib/constants';

const labels = new LocalizedStrings(localisedText);

// Must match backend AuthTypeEnum exactly
const AUTH_TYPES = [
  { value: 'API_KEY', label: 'API Key' },
  { value: 'BEARER_TOKEN', label: 'Bearer Token' },
  { value: 'BASIC_AUTH', label: 'Basic Auth' },
  { value: 'HMAC', label: 'HMAC' },
  { value: 'OAUTH2_CLIENT_CREDENTIALS', label: 'OAuth2 Client Credentials' },
  { value: 'MTLS', label: 'mTLS' },
];

// Must match backend BearerTokenAuthModeEnum exactly
const BEARER_TOKEN_MODES = [
  { value: 'static', label: 'Static Token' },
  { value: 'signed_jwt', label: 'Signed JWT' },
  { value: 'fetched', label: 'Fetched' },
];

export class AuthConfigModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      auth_type: '',
      authConfig: {},
      bearerTokenMode: 'static',
    };
  }

  componentDidMount() {
    this.populateFromSubscription(this.props.subscription);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.subscription !== this.props.subscription) {
      this.populateFromSubscription(this.props.subscription);
    }
  }

  populateFromSubscription(subscription) {
    if (!subscription) {
      this.setState({ auth_type: '', authConfig: {}, bearerTokenMode: 'static' });
      return;
    }

    const ac = subscription.auth_config;
    if (!ac || !ac.type) {
      this.setState({ auth_type: '', authConfig: {}, bearerTokenMode: 'static' });
      return;
    }

    let authConfig = {};
    let bearerTokenMode = 'static';

    switch (ac.type) {
      case 'API_KEY':
        authConfig = { header_name: ac.header_name || '', api_key: ac.api_key || '' };
        break;
      case 'BEARER_TOKEN':
        bearerTokenMode = ac.mode || 'static';
        authConfig = {
          token: ac.token || '',
          signing_algorithm: ac.signing_algorithm || 'RS256',
          private_key: ac.private_key || '',
          subject: ac.subject || '',
          issuer: ac.issuer || '',
          audience: ac.audience || '',
          expires_in: ac.expires_in || '',
          token_endpoint: ac.token_endpoint || '',
          token_response_path: ac.token_response_path || '',
          token_request_payload_raw: ac.token_request_payload
            ? JSON.stringify(ac.token_request_payload, null, 2) : '',
          token_expiring_response_path: ac.token_expiring_response_path || '',
        };
        break;
      case 'BASIC_AUTH':
        authConfig = { username: ac.username || '', password: ac.password || '' };
        break;
      case 'HMAC':
        authConfig = {
          algorithm: ac.algorithm || 'sha256',
          secret: ac.secret || '',
          header_name: ac.header_name || '',
          include_timestamp: ac.include_timestamp || false,
        };
        break;
      case 'OAUTH2_CLIENT_CREDENTIALS':
        authConfig = {
          token_url: ac.token_url || '',
          client_id: ac.client_id || '',
          client_secret: ac.client_secret || '',
          scope: ac.scope || '',
          audience: ac.audience || '',
        };
        break;
      case 'MTLS':
        authConfig = {
          client_cert: ac.client_cert || '',
          client_key: ac.client_key || '',
          ca_cert: ac.ca_cert || '',
        };
        break;
      default:
        break;
    }

    this.setState({ auth_type: ac.type, authConfig, bearerTokenMode });
  }

  handleAuthTypeChange = (e) => {
    this.setState({ auth_type: e.target.value, authConfig: {}, bearerTokenMode: 'static' });
  };

  handleAuthFieldChange = (field, value) => {
    this.setState((prev) => ({ authConfig: { ...prev.authConfig, [field]: value } }));
  };

  buildAuthConfig = () => {
    const { auth_type, authConfig, bearerTokenMode } = this.state;
    if (!auth_type) return null;

    const config = { type: auth_type };

    switch (auth_type) {
      case 'API_KEY':
        config.header_name = authConfig.header_name || '';
        config.api_key = authConfig.api_key || '';
        break;
      case 'BEARER_TOKEN':
        config.mode = bearerTokenMode;
        if (bearerTokenMode === 'static') {
          config.token = authConfig.token || '';
        } else if (bearerTokenMode === 'signed_jwt') {
          config.signing_algorithm = authConfig.signing_algorithm || 'RS256';
          config.private_key = authConfig.private_key || '';
          if (authConfig.subject) config.subject = authConfig.subject;
          if (authConfig.issuer) config.issuer = authConfig.issuer;
          if (authConfig.audience) config.audience = authConfig.audience;
          if (authConfig.expires_in) config.expires_in = parseInt(authConfig.expires_in, 10);
        } else if (bearerTokenMode === 'fetched') {
          config.token_endpoint = authConfig.token_endpoint || '';
          config.token_response_path = authConfig.token_response_path || '';
          if (authConfig.token_request_payload_raw) {
            try { config.token_request_payload = JSON.parse(authConfig.token_request_payload_raw); }
            catch (_) { config.token_request_payload = {}; }
          }
          if (authConfig.token_expiring_response_path) {
            config.token_expiring_response_path = authConfig.token_expiring_response_path;
          }
        }
        break;
      case 'BASIC_AUTH':
        config.username = authConfig.username || '';
        config.password = authConfig.password || '';
        break;
      case 'HMAC':
        config.algorithm = authConfig.algorithm || 'sha256';
        config.secret = authConfig.secret || '';
        config.header_name = authConfig.header_name || '';
        if (authConfig.include_timestamp !== undefined) {
          config.include_timestamp = authConfig.include_timestamp;
        }
        break;
      case 'OAUTH2_CLIENT_CREDENTIALS':
        config.token_url = authConfig.token_url || '';
        config.client_id = authConfig.client_id || '';
        config.client_secret = authConfig.client_secret || '';
        if (authConfig.scope) config.scope = authConfig.scope;
        if (authConfig.audience) config.audience = authConfig.audience;
        break;
      case 'MTLS':
        config.client_cert = authConfig.client_cert || '';
        config.client_key = authConfig.client_key || '';
        if (authConfig.ca_cert) config.ca_cert = authConfig.ca_cert;
        break;
      default:
        break;
    }

    return config;
  };

  handleSave = () => {
    const { subscription, onSave } = this.props;
    if (!subscription) return;
    const authConfig = this.buildAuthConfig();
    if (authConfig) onSave(subscription.guid, authConfig);
  };

  handleRemove = () => {
    const { subscription, onRemove } = this.props;
    if (!subscription) return;
    onRemove(subscription.guid);
  };

  renderAuthFields = () => {
    const { auth_type, authConfig, bearerTokenMode } = this.state;
    if (!auth_type) return null;
    const upd = (field, val) => this.handleAuthFieldChange(field, val);

    switch (auth_type) {

      case 'API_KEY':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Header Name" placeholder="x-api-key"
                value={authConfig.header_name || ''} onChange={(e) => upd('header_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="API Key" type="password"
                value={authConfig.api_key || ''} onChange={(e) => upd('api_key', e.target.value)} />
            </Grid>
          </Grid>
        );

      case 'BEARER_TOKEN':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Token Mode</InputLabel>
                <Select value={bearerTokenMode} label="Token Mode"
                  onChange={(e) => this.setState({ bearerTokenMode: e.target.value, authConfig: {} })}>
                  {BEARER_TOKEN_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {bearerTokenMode === 'static' && (
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Static Token" type="password"
                  value={authConfig.token || ''} onChange={(e) => upd('token', e.target.value)} />
              </Grid>
            )}

            {bearerTokenMode === 'signed_jwt' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Signing Algorithm</InputLabel>
                    <Select value={authConfig.signing_algorithm || 'RS256'} label="Signing Algorithm"
                      onChange={(e) => upd('signing_algorithm', e.target.value)}>
                      <MenuItem value="RS256">RS256</MenuItem>
                      <MenuItem value="HS256">HS256</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" label="Private Key (PEM)" type="password" multiline rows={3}
                    value={authConfig.private_key || ''} onChange={(e) => upd('private_key', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth variant="outlined" label="Subject (optional)"
                    value={authConfig.subject || ''} onChange={(e) => upd('subject', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth variant="outlined" label="Issuer (optional)"
                    value={authConfig.issuer || ''} onChange={(e) => upd('issuer', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth variant="outlined" label="Audience (optional)"
                    value={authConfig.audience || ''} onChange={(e) => upd('audience', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth variant="outlined" label="Expires In (seconds, optional)" type="number"
                    value={authConfig.expires_in || ''} onChange={(e) => upd('expires_in', e.target.value)} />
                </Grid>
              </>
            )}

            {bearerTokenMode === 'fetched' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" label="Token Endpoint URL"
                    value={authConfig.token_endpoint || ''} onChange={(e) => upd('token_endpoint', e.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" label="Token Response Path" placeholder="data.access_token"
                    value={authConfig.token_response_path || ''} onChange={(e) => upd('token_response_path', e.target.value)}
                    helperText="Dot-notation path to extract the token from the response" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" label="Token Request Payload (JSON)" multiline rows={3}
                    placeholder='{"client_id": "...", "client_secret": "..."}'
                    value={authConfig.token_request_payload_raw || ''}
                    onChange={(e) => upd('token_request_payload_raw', e.target.value)}
                    helperText="JSON object sent in the token request body" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth variant="outlined" label="Token Expiry Response Path (optional)" placeholder="data.expires_at"
                    value={authConfig.token_expiring_response_path || ''}
                    onChange={(e) => upd('token_expiring_response_path', e.target.value)} />
                </Grid>
              </>
            )}
          </Grid>
        );

      case 'BASIC_AUTH':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Username"
                value={authConfig.username || ''} onChange={(e) => upd('username', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Password" type="password"
                value={authConfig.password || ''} onChange={(e) => upd('password', e.target.value)} />
            </Grid>
          </Grid>
        );

      case 'HMAC':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Hashing Algorithm</InputLabel>
                <Select value={authConfig.algorithm || 'sha256'} label="Hashing Algorithm"
                  onChange={(e) => upd('algorithm', e.target.value)}>
                  <MenuItem value="sha256">SHA-256</MenuItem>
                  <MenuItem value="sha512">SHA-512</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Secret" type="password"
                value={authConfig.secret || ''} onChange={(e) => upd('secret', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Signature Header Name" placeholder="X-Signature"
                value={authConfig.header_name || ''} onChange={(e) => upd('header_name', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={authConfig.include_timestamp || false} color="primary"
                  onChange={(e) => upd('include_timestamp', e.target.checked)} />}
                label="Include Timestamp in Signature"
              />
            </Grid>
          </Grid>
        );

      case 'OAUTH2_CLIENT_CREDENTIALS':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Token URL"
                value={authConfig.token_url || ''} onChange={(e) => upd('token_url', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Client ID"
                value={authConfig.client_id || ''} onChange={(e) => upd('client_id', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Client Secret" type="password"
                value={authConfig.client_secret || ''} onChange={(e) => upd('client_secret', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth variant="outlined" label="Scope (optional)"
                value={authConfig.scope || ''} onChange={(e) => upd('scope', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth variant="outlined" label="Audience (optional)"
                value={authConfig.audience || ''} onChange={(e) => upd('audience', e.target.value)} />
            </Grid>
          </Grid>
        );

      case 'MTLS':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Client Certificate (PEM)" multiline rows={4}
                value={authConfig.client_cert || ''} onChange={(e) => upd('client_cert', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Client Private Key (PEM)" multiline rows={4}
                value={authConfig.client_key || ''} onChange={(e) => upd('client_key', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="CA Certificate (PEM, optional)" multiline rows={4}
                value={authConfig.ca_cert || ''} onChange={(e) => upd('ca_cert', e.target.value)} />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  render() {
    const { open, onClose, subscription, loading, errors } = this.props;
    const { auth_type } = this.state;

    const hasExistingAuth = subscription && subscription.auth_config && subscription.auth_config.type;

    const eventLabel = subscription
      ? (Array.isArray(subscription.event_types)
          ? subscription.event_types.join(', ')
          : subscription.event_types || '')
      : '';

    return (
      <Dialog open={open} onClose={onClose} fullScreen>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {labels.editAuth || 'Edit Authentication'}
              {eventLabel ? (
                <Typography variant="body2" component="span" style={{ marginLeft: 8, opacity: 0.8 }}>
                  — {eventLabel}
                </Typography>
              ) : null}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <div style={{ marginTop: '2%' }}>
            {errors && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {typeof errors === 'string' ? errors : JSON.stringify(errors)}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Current auth type info */}
              {hasExistingAuth && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {labels.currentAuthType || 'Current auth type'}: <strong>{subscription.auth_config.type}</strong>
                  </Alert>
                </Grid>
              )}

              {/* Auth Type selector */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{labels.authType || 'Authentication Type'}</InputLabel>
                  <Select value={auth_type} label={labels.authType || 'Authentication Type'}
                    onChange={this.handleAuthTypeChange}>
                    <MenuItem value=""><em>{labels.selectAuthType || 'None'}</em></MenuItem>
                    {AUTH_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Auth fields */}
              {auth_type && (
                <Grid item xs={12}>
                  {this.renderAuthFields()}
                </Grid>
              )}

              {/* Action buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
                  <Button onClick={onClose} disabled={loading}>
                    {labels.cancel || 'Cancel'}
                  </Button>
                  {hasExistingAuth && (
                    <Button onClick={this.handleRemove} disabled={loading} color="secondary" variant="outlined">
                      {loading ? <CircularProgress size={20} /> : (labels.deleteAuth || 'Delete Auth')}
                    </Button>
                  )}
                  <Button
                    onClick={this.handleSave}
                    disabled={loading || !auth_type}
                    variant="contained"
                    style={{ background: loading || !auth_type ? undefined : PRIMARY_THEME_COLOR_SHADE_1 }}
                  >
                    {loading ? <CircularProgress size={20} /> : (labels.updateAuth || 'Update Auth')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}
